import type { TrainingClassSession } from '../../types';
import { Button } from '@workramp/ui';
import { useMemo } from 'react';
import { I18n } from '~/common/explicit-exports';
import { useLearnerEventDetails } from '../../hooks/useLearnerEventDetails';

const TRANSLATED_REGISTER = I18n.t('js.learner.events.sessions.register');
const TRANSLATED_DONE = I18n.t('common.application.done');
const TRANSLATED_NO_SEATS_AVAILABLE = I18n.t('js.learner.events.sessions.action_button.no_seats_available');
const TRANSLATED_SESSION_IN_PAST = I18n.t('js.learner.events.sessions.action_button.session_in_past');
const TRANSLATED_JOIN_WAITLIST = I18n.t('js.learner.events.sessions.join_waitlist');
const TRANSLATED_MARK_MY_ATTENDANCE = I18n.t('js.learner.events.sessions.mark_my_attendance');
const TRANSLATED_UNMARK_MY_ATTENDANCE = I18n.t('js.learner.events.sessions.unmark_my_attendance');

type Props = {
  session: TrainingClassSession;
  isLearnerRegistered: boolean;
  isLearnerWaitlisted: boolean;
  isEventSeries: boolean;
  canLearnerMarkAttendance: boolean;
  canLearnerUnmarkAttendance: boolean;
  canLearnerJoinWaitlist: boolean;
  canLearnerNotJoinWaitlist: boolean;
  canLearnerRegister: boolean;
  onClose: () => void;
};

export const PrimaryButton = ({
  session,
  isLearnerRegistered,
  isLearnerWaitlisted,
  isEventSeries,
  canLearnerMarkAttendance,
  canLearnerUnmarkAttendance,
  canLearnerJoinWaitlist,
  canLearnerNotJoinWaitlist,
  canLearnerRegister,
  onClose,
}: Props): JSX.Element => {
  const { onJoinWaitlist, onRegister, onMarkAttendance, onUnmarkAttendance } = useLearnerEventDetails();

  const [primaryButtonLabel, onPressPrimary] = useMemo<[string, () => void]>(() => {
    if (isEventSeries) {
      return [TRANSLATED_DONE, onClose];
    }

    if (canLearnerMarkAttendance) {
      return [TRANSLATED_MARK_MY_ATTENDANCE, () => onMarkAttendance(session.id)];
    }

    if (canLearnerUnmarkAttendance) {
      return [TRANSLATED_UNMARK_MY_ATTENDANCE, () => onUnmarkAttendance(session.id)];
    }

    if (isLearnerRegistered || isLearnerWaitlisted) {
      return [TRANSLATED_DONE, onClose];
    }

    if (canLearnerJoinWaitlist) {
      return [TRANSLATED_JOIN_WAITLIST, () => onJoinWaitlist(session.id)];
    }

    if (canLearnerNotJoinWaitlist) {
      return [TRANSLATED_NO_SEATS_AVAILABLE, null];
    }

    if (!canLearnerRegister) {
      return [TRANSLATED_DONE, onClose];
    }

    return [TRANSLATED_REGISTER, () => onRegister(session.id)];
  }, [
    canLearnerJoinWaitlist,
    canLearnerMarkAttendance,
    canLearnerNotJoinWaitlist,
    canLearnerRegister,
    canLearnerUnmarkAttendance,
    isEventSeries,
    isLearnerRegistered,
    isLearnerWaitlisted,
    onClose,
    onJoinWaitlist,
    onMarkAttendance,
    onRegister,
    onUnmarkAttendance,
    session.id,
  ]);

  const sessionInPast = session.end && Date.now() > session.end;
  const primaryButtonDisabled =
    !onPressPrimary ||
    (sessionInPast && (primaryButtonLabel === TRANSLATED_REGISTER || primaryButtonLabel === TRANSLATED_JOIN_WAITLIST));
  const tooltip = primaryButtonDisabled ? TRANSLATED_SESSION_IN_PAST : undefined;

  return (
    <Button onPress={onPressPrimary} isDisabled={primaryButtonDisabled} tooltip={tooltip}>
      {primaryButtonLabel}
    </Button>
  );
};
